
import type { BagSlotModel } from "./bag";
import { itemArtNode } from "./itemart";
import { tooltipLines, type ItemTooltipData } from "./tooltip";
import { elem } from "./chrome";

export function isAttachableSpiritStoneVnum(vnum: number): boolean {
  if (!Number.isInteger(vnum)) return false;
  const grade = Math.floor((vnum - 28_000) / 100);
  const suffix = vnum % 100;
  if (grade >= 0 && grade <= 4) {
    return suffix === 0 || suffix === 4 || suffix === 8 || suffix === 12 ||
      (suffix >= 30 && suffix <= 43);
  }
  return grade === 5 && (suffix === 0 || suffix === 4 || suffix === 8 || suffix === 12);
}

function targetKindsFor(vnum: number): readonly number[] {
  const grade = Math.floor((vnum - 28_000) / 100);
  const suffix = vnum % 100;
  if (grade === 5) {
    if (suffix === 0) return [2];
    if (suffix === 4 || suffix === 8 || suffix === 12) return [1, 2];
    return [];
  }
  if (grade < 0 || grade > 4) return [];
  return suffix >= 38 && suffix <= 43 ? [2] : [1];
}

export function projectedMetinTarget(
  stone: BagSlotModel,
  target: BagSlotModel,
): BagSlotModel | null {
  if (!isAttachableSpiritStoneVnum(stone.vnum)) return null;
  if (!targetKindsFor(stone.vnum).includes(target.kind)) return null;
  const sockets = [target.socket ?? 0, target.socket1 ?? 0, target.socket2 ?? 0];
  // Only an opened socket takes a stone: GetSocket(i) >= 1 && <= 2 (`char_item.cpp:4848`).
  const vacant = sockets.findIndex((socket) => socket === 1 || socket === 2);
  if (vacant < 0) return null;
  sockets[vacant] = stone.vnum;
  return { ...target, socket: sockets[0], socket1: sockets[1], socket2: sockets[2] };
}

function tooltipData(slot: BagSlotModel): ItemTooltipData {
  return {
    ...slot,
    price: slot.sellValue,
  };
}

function drawTooltip(doc: Document, slot: BagSlotModel): HTMLElement {
  const root = elem(doc, "div", "attach-metin-tooltip");
  for (const line of tooltipLines(tooltipData(slot))) {
    const row = elem(doc, "div", `item-tooltip-line item-tooltip-line-${line.tone}`, line.text);
    root.append(row);
  }
  return root;
}

export type AttachMetinAction = (stoneCell: number, targetCell: number) => void;

/** One modal confirmation.  `open` returns false when this is not the legacy
 * stone-on-compatible-equipment gesture, allowing the bag to keep routing keys,
 * scrolls, accessory materials and server-side refusals unchanged. */
export class AttachMetinPanel {
  private readonly root: HTMLElement;
  private readonly stoneArt: HTMLElement;
  private readonly before: HTMLElement;
  private readonly after: HTMLElement;
  private pending: { readonly stone: number; readonly target: number } | null = null;

  constructor(container: HTMLElement, private readonly onAccept: AttachMetinAction) {
    const doc = container.ownerDocument;
    this.root = elem(doc, "section", "attach-metin");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");

    const title = elem(doc, "h2", "attach-metin-title", "Use Spirit Stone");
    this.root.setAttribute("aria-labelledby", "attach-metin-title");
    title.id = "attach-metin-title";

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "attach-metin-close";
    close.textContent = "×";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.close());

    const info = elem(doc, "p", "attach-metin-info", "Do you want to use the Spirit Stone?");

    this.stoneArt = elem(doc, "div", "attach-metin-stone");

    const comparison = elem(doc, "div", "attach-metin-comparison");
    const beforeColumn = doc.createElement("section");
    const beforeTitle = doc.createElement("h3");
    beforeTitle.textContent = "Before";
    this.before = doc.createElement("div");
    beforeColumn.append(beforeTitle, this.before);
    const arrow = elem(doc, "div", "attach-metin-arrow", "→");
    arrow.setAttribute("aria-hidden", "true");
    const afterColumn = doc.createElement("section");
    const afterTitle = doc.createElement("h3");
    afterTitle.textContent = "After";
    this.after = doc.createElement("div");
    afterColumn.append(afterTitle, this.after);
    comparison.append(beforeColumn, arrow, afterColumn);

    const actions = elem(doc, "div", "attach-metin-actions");
    const accept = doc.createElement("button");
    accept.type = "button";
    accept.className = "attach-metin-accept";
    accept.textContent = "Accept";
    accept.addEventListener("click", () => this.accept());
    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.className = "attach-metin-cancel";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.close());
    actions.append(accept, cancel);

    this.root.append(title, close, info, this.stoneArt, comparison, actions);
    container.append(this.root);
    doc.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || this.root.hidden) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  open(stone: BagSlotModel, target: BagSlotModel): boolean {
    const projected = projectedMetinTarget(stone, target);
    if (projected === null) return false;
    const doc = this.root.ownerDocument;
    this.pending = { stone: stone.slot, target: target.slot };
    // The stone comes out of the bag, so its `kind` has been on hand the
    // whole time; this cell drew a lozenge for want of asking for it.
    this.stoneArt.replaceChildren(itemArtNode(stone.vnum, stone.kind, doc));
    this.stoneArt.setAttribute("aria-label", stone.name);
    this.before.replaceChildren(drawTooltip(doc, target));
    this.after.replaceChildren(drawTooltip(doc, projected));
    this.root.hidden = false;
    return true;
  }

  close(): void {
    this.pending = null;
    this.root.hidden = true;
  }

  private accept(): void {
    const pending = this.pending;
    if (pending === null) return;
    // `uiattachmetin.py:130-133`: the click sends only the remembered source
    // and target cells, plays its local sound, then closes.  The browser's sound
    // remains the shared Bag/Notice answer; the trust-boundary shape is exact.
    this.close();
    this.onAccept(pending.stone, pending.target);
  }
}
